import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

const COLOR_PRESETS = [
  'ocean-blue', 'forest-green', 'sunset-amber', 'slate-professional',
  'crimson-bold', 'teal-fresh', 'indigo-modern', 'emerald-trust',
  'warm-earth', 'cool-steel', 'royal-purple', 'coral-energy',
  'sage-calm', 'navy-classic', 'charcoal-minimal', 'sky-light',
];

const FONT_PRESETS = [
  'modern', 'classic', 'bold', 'friendly', 'geometric', 'professional',
  'elegant', 'tech', 'readable', 'minimal', 'editorial', 'refined',
  'artisan', 'magazine', 'dignified', 'utility', 'impact', 'industrial',
  'heritage', 'precision', 'billboard', 'expressive', 'avant-garde',
  'dynamic', 'polished', 'design-forward', 'studio',
];

interface Props {
  data: any;
  onChange: (data: any) => void;
}

/** Human-readable labels for layout style dropdowns */
const STYLE_FIELD_LABELS: Record<string, string> = {
  heroStyle:         'Top banner layout',
  cardStyle:         'Card style',
  radius:            'Corner rounding',
  headerStyle:       'Header style',
  serviceStyle:      'Services layout',
  footerStyle:       'Footer style',
  faqStyle:          'FAQ layout',
  testimonialsStyle: 'Reviews layout',
  ctaStyle:          'Call-to-action banner style',
  processStyle:      'Process / "How it works" style',
};

const styleOptions: Record<string, string[]> = {
  heroStyle:         ['split', 'centered', 'fullwidth', 'angled', 'minimal'],
  cardStyle:         ['elevated', 'flat', 'bordered', 'glass', 'minimal'],
  radius:            ['none', 'sm', 'md', 'lg', 'full'],
  headerStyle:       ['solid', 'transparent', 'floating', 'minimal'],
  serviceStyle:      ['cards', 'list', 'grid', 'accordion'],
  footerStyle:       ['standard', 'minimal', 'expanded', 'dark'],
  faqStyle:          ['accordion', 'two-column', 'cards', 'minimal'],
  testimonialsStyle: ['carousel', 'grid', 'featured', 'minimal', 'wall'],
  ctaStyle:          ['standard', 'centered', 'card', 'minimal'],
  processStyle:      ['cards', 'timeline', 'numbered'],
};

/** Human-readable labels for visual effects checkboxes */
const VISUAL_EFFECT_LABELS: Record<string, string> = {
  glassEffect:      'Glass / frosted effect on cards',
  meshBackground:   'Mesh gradient backgrounds',
  gradientButtons:  'Gradient-colored buttons',
  gradientText:     'Gradient-colored headlines',
};

const ThemeEditor: React.FC<Props> = ({ data, onChange }) => {
  const form = data || {};

  const update = (field: string, value: any) => {
    onChange({ ...form, [field]: value || undefined });
  };

  const selectField = (label: string, field: string, options: string[]) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={form[field] || undefined} onValueChange={(v) => update(field, v)}>
        <SelectTrigger>
          <SelectValue placeholder="Default" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-display font-semibold text-primary">Theme &amp; Style</h2>
        <p className="text-sm text-muted mt-1 mb-5">Colors, fonts, and visual appearance of your site</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Colors &amp; Fonts</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {selectField('Color scheme', 'colors', COLOR_PRESETS)}
          {selectField('Font style', 'fonts', FONT_PRESETS)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Layout &amp; Section Styles</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(styleOptions).map(([field, opts]) => (
            <React.Fragment key={field}>
              {selectField(STYLE_FIELD_LABELS[field] ?? field, field, opts)}
            </React.Fragment>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Visual Effects</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Gradient style</Label>
            <Select value={form.gradientStyle_ve || undefined} onValueChange={(v) => update('gradientStyle_ve', v)}>
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                {['none', 'subtle', 'bold', 'radial'].map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1">
            {Object.entries(VISUAL_EFFECT_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-primary cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.visualEffects?.[key]}
                  onChange={(e) => onChange({
                    ...form,
                    visualEffects: { ...form.visualEffects, [key]: e.target.checked },
                  })}
                  className="rounded border-zinc-300 text-accent focus:ring-primary"
                />
                {label}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ThemeEditor;
